import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Center,
  ChakraProvider,
  Container,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useToast,
} from '@chakra-ui/react';
import AdminHeader from '../components/AdminHeader';
import { getVisitorVisits } from '../visitors/visitorApi';

function formatVisitedAt(value) {
  if (!value) return '—';
  const date = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function VisitorListings() {
  const navigate = useNavigate();
  const toast = useToast();
  const [visits, setVisits] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [visitorType, setVisitorType] = useState('all');
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState({
    total_records: 0,
    total_pages: 1,
    current_page: 1,
    has_next_page: false,
    has_prev_page: false,
  });

  useEffect(() => {
    document.title = 'Visitor Activity';
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadVisits = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getVisitorVisits({
        page,
        limit: 20,
        search: debouncedSearch,
        filter,
        visitorType,
      });
      setVisits(Array.isArray(data.visits) ? data.visits : []);
      setPagination({
        total_records: data.pagination?.total_records || 0,
        total_pages: data.pagination?.total_pages || 1,
        current_page: data.pagination?.current_page || page,
        has_next_page: data.pagination?.has_next_page || false,
        has_prev_page: data.pagination?.has_prev_page || false,
      });
    } catch (error) {
      setVisits([]);
      toast({
        description: error.message || 'Unable to load visitor activity.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
      if (/authentication/i.test(error.message || '')) {
        navigate('/Admin');
      }
    } finally {
      setIsLoading(false);
    }
  }, [page, debouncedSearch, filter, visitorType, navigate, toast]);

  useEffect(() => {
    if (!localStorage.getItem('adminToken')) {
      navigate('/Admin');
      return;
    }
    loadVisits();
  }, [loadVisits, navigate]);

  const changePage = (nextPage) => {
    const totalPages = pagination.total_pages || 1;
    if (nextPage < 1 || nextPage > totalPages) return;
    setPage(nextPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const totalPages = pagination.total_pages || 1;

  return (
    <ChakraProvider>
      <Box minH="100vh" bg="gray.50">
        <AdminHeader
          activeTab="visitors"
          title="Visitor Activity"
          badge={
            <Badge colorScheme="blue" fontSize="sm" px={3} py={1} borderRadius="full">
              {pagination.total_records} Visits
            </Badge>
          }
        />
        <Container maxW="7xl" pb={8}>
          <VStack spacing={6} align="stretch">
            <Card borderRadius="lg" boxShadow="md" border="1px solid" borderColor="gray.200">
              <CardBody p={6}>
                <Text color="gray.600" mb={4}>
                  Each row is a page visit. New and returning visitors are recognized by a random id stored in the browser, not by IP address.
                </Text>
                <Flex gap={4} flexWrap="wrap">
                  <InputGroup flex="1" minW="260px">
                    <InputLeftElement pointerEvents="none" color="gray.400">
                      Search
                    </InputLeftElement>
                    <Input
                      pl="4.5rem"
                      placeholder="Search IP, browser, system, or page"
                      value={searchTerm}
                      onChange={(event) => {
                        setSearchTerm(event.target.value);
                        setPage(1);
                      }}
                      borderRadius="md"
                      border="2px solid"
                      borderColor="gray.200"
                      _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182ce' }}
                    />
                  </InputGroup>
                  <Select
                    maxW="200px"
                    value={visitorType}
                    aria-label="Filter by visitor type"
                    onChange={(event) => {
                      setVisitorType(event.target.value);
                      setPage(1);
                    }}
                    borderRadius="md"
                    border="2px solid"
                    borderColor="gray.200"
                  >
                    <option value="all">All visitors</option>
                    <option value="new">New</option>
                    <option value="returning">Returning</option>
                  </Select>
                  <Select
                    maxW="200px"
                    value={filter}
                    aria-label="Filter by date"
                    onChange={(event) => {
                      setFilter(event.target.value);
                      setPage(1);
                    }}
                    borderRadius="md"
                    border="2px solid"
                    borderColor="gray.200"
                  >
                    <option value="all">All dates</option>
                    <option value="today">Today</option>
                    <option value="7days">Last 7 days</option>
                    <option value="30days">Last 30 days</option>
                    <option value="90days">Last 90 days</option>
                  </Select>
                </Flex>
              </CardBody>
            </Card>

            <Card borderRadius="lg" boxShadow="md" border="1px solid" borderColor="gray.200" overflow="hidden">
              <Box overflowX="auto">
                <Table variant="simple" size="md">
                  <Thead bg="blue.50">
                    <Tr>
                      {['Visitor', 'Status', 'IP Address', 'Device', 'Browser', 'Operating System', 'Page', 'Visited'].map((heading) => (
                        <Th key={heading} color="gray.700" fontWeight="700" fontSize="sm" py={4}>
                          {heading}
                        </Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {isLoading ? (
                      <Tr>
                        <Td colSpan={8}>
                          <Center py={10}>
                            <VStack>
                              <Spinner color="blue.500" />
                              <Text color="gray.500">Loading visitor activity...</Text>
                            </VStack>
                          </Center>
                        </Td>
                      </Tr>
                    ) : visits.length === 0 ? (
                      <Tr>
                        <Td colSpan={8} textAlign="center" py={12}>
                          <Text color="gray.500" fontWeight="600">No visits found</Text>
                          <Text color="gray.400" fontSize="sm" mt={1}>
                            {searchTerm || filter !== 'all' || visitorType !== 'all'
                              ? 'Try a different search or filter.'
                              : 'Visits appear here after someone opens the homepage.'}
                          </Text>
                        </Td>
                      </Tr>
                    ) : (
                      visits.map((visit) => (
                        <Tr key={visit.id} _hover={{ bg: 'blue.50' }}>
                          <Td color="gray.600">#{visit.visitor_ref}</Td>
                          <Td>
                            <Badge colorScheme={visit.is_new ? 'green' : 'purple'}>
                              {visit.is_new ? 'New' : 'Returning'}
                            </Badge>
                          </Td>
                          <Td>{visit.ip_address || '—'}</Td>
                          <Td>{visit.device_type || '—'}</Td>
                          <Td>{visit.browser || '—'}</Td>
                          <Td>{visit.operating_system || '—'}</Td>
                          <Td>{visit.page_path || '—'}</Td>
                          <Td fontSize="sm" color="gray.600" whiteSpace="nowrap">
                            {formatVisitedAt(visit.visited_at)}
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>
            </Card>

            {totalPages > 1 && (
              <Card borderRadius="lg" boxShadow="sm" border="1px solid" borderColor="gray.200">
                <CardBody>
                  <HStack justify="center" spacing={2} flexWrap="wrap">
                    <Button onClick={() => changePage(page - 1)} isDisabled={!pagination.has_prev_page || isLoading}>
                      Prev
                    </Button>
                    <Text fontSize="sm" color="gray.600">
                      Page {pagination.current_page} of {totalPages}
                    </Text>
                    <Button onClick={() => changePage(page + 1)} isDisabled={!pagination.has_next_page || isLoading}>
                      Next
                    </Button>
                  </HStack>
                </CardBody>
              </Card>
            )}
          </VStack>
        </Container>
      </Box>
    </ChakraProvider>
  );
}

export default VisitorListings;
